"use strict";

/**
 * audioChannelDownmix
 * -------------------
 * Collapses a decoded multi-channel buffer to mono by averaging all channels and
 * writing the mono mix into every channel. This is the "center everyone" fix for
 * recordings where a voice is panned hard to one side — e.g. an interview where a
 * guest is only in the left ear, or a file whose sound sits on a single channel.
 * After the downmix every voice is audible in both ears.
 *
 * This is a deliberate, lossy operation (stereo separation is discarded), so it is
 * strictly opt-in — the caller only invokes it when the ?forceMonoAudio=true URL
 * flag is set. It is never applied to a file by default.
 *
 * Averaging keeps the result within full-scale, so it cannot clip. Any per-channel
 * level lost by averaging a one-sided file is recovered by the peak-normalization
 * step that runs later in the decode pipeline.
 *
 * The function is dependency-free and side-effect-free (it returns a new buffer and
 * never mutates the input) so it can be unit-tested in plain Node without a Web
 * Audio context. The module is CommonJS so `node:test` can require it directly;
 * webpack imports it via its normal CJS/ESM interop.
 */

/**
 * A mono mix whose peak is below this floor is treated as silence (e.g. produced by
 * phase-inverted channels that cancel). Downmixing to it would break the downstream
 * peak-normalization step, so the downmix is declined instead.
 */
const SILENT_MONO_PEAK_FLOOR = 1e-6;

/**
 * @param {AudioBuffer} audioBuffer decoded source buffer
 * @param {(numberOfChannels:number, length:number, sampleRate:number)=>AudioBuffer} createBuffer
 *        factory (e.g. `audioContext.createBuffer.bind(audioContext)`)
 * @returns {AudioBuffer} a new mono-in-every-channel buffer, or the original if already
 *          mono or if the channels cancel to silence
 */
function downmixToMono(audioBuffer, createBuffer) {
    const numberOfChannels = audioBuffer.numberOfChannels;
    if (numberOfChannels < 2) {
        return audioBuffer; // already mono — nothing to do
    }

    const length = audioBuffer.length;
    const channelData = [];
    for (let ch = 0; ch < numberOfChannels; ch++) {
        channelData.push(audioBuffer.getChannelData(ch));
    }

    const mono = new Float32Array(length);
    let monoPeak = 0;
    for (let i = 0; i < length; i++) {
        let sum = 0;
        for (let ch = 0; ch < numberOfChannels; ch++) {
            sum += channelData[ch][i];
        }
        mono[i] = sum / numberOfChannels;
        const abs = mono[i] < 0 ? -mono[i] : mono[i];
        if (abs > monoPeak) {
            monoPeak = abs;
        }
    }

    // If the channels cancel out (e.g. phase-inverted L = -R), the mono mix is
    // effectively silence. Emitting a zero buffer would make the downstream peak-
    // normalization step divide by zero and fill the buffer with NaN, breaking
    // playback/export. Decline the downmix and keep the original audio instead.
    if (monoPeak < SILENT_MONO_PEAK_FLOOR) {
        return audioBuffer;
    }

    const downmixed = createBuffer(numberOfChannels, length, audioBuffer.sampleRate);
    for (let ch = 0; ch < numberOfChannels; ch++) {
        downmixed.copyToChannel(mono, ch);
    }
    return downmixed;
}

module.exports = {
    downmixToMono,
};
